import React from 'react';
import { CheckCircle2, ChevronRight, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CampaignStepDefinition {
    id: number;
    label: string;
    icon: LucideIcon;
}

export interface CampaignStepperProps {
    steps: CampaignStepDefinition[];
    currentStep: number;
    /** The furthest step reached so far — steps up to here can be clicked to jump back. */
    furthestStep: number;
    onStepClick?: (step: number) => void;
}

/**
 * Horizontal step indicator for the campaign editor, mirroring the visual
 * language of `EnhancedImportWizard`'s progress bar (numbered circles joined
 * by chevrons) but generalized to an arbitrary step list and clickable for
 * steps already reached.
 */
export const CampaignStepper: React.FC<CampaignStepperProps> = ({
    steps,
    currentStep,
    furthestStep,
    onStepClick,
}) => {
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/50 p-4">
            {steps.map((step, index) => {
                const isComplete = step.id < currentStep;
                const isCurrent = step.id === currentStep;
                const isReachable = step.id <= furthestStep;
                const Icon = step.icon;

                return (
                    <React.Fragment key={step.id}>
                        <button
                            type="button"
                            disabled={!isReachable || !onStepClick}
                            onClick={() => onStepClick?.(step.id)}
                            className={cn(
                                'flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors',
                                isReachable && onStepClick
                                    ? 'cursor-pointer hover:bg-background'
                                    : '',
                                !isReachable && 'cursor-not-allowed opacity-50'
                            )}
                        >
                            <div
                                className={cn(
                                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-medium',
                                    isCurrent
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : isComplete
                                          ? 'border-primary/60 bg-primary/10 text-primary'
                                          : 'border-border text-muted-foreground'
                                )}
                            >
                                {isComplete ? (
                                    <CheckCircle2 className="h-4 w-4" />
                                ) : (
                                    <Icon className="h-4 w-4" />
                                )}
                            </div>
                            <span
                                className={cn(
                                    'text-sm font-medium',
                                    isCurrent ? 'text-foreground' : 'text-muted-foreground'
                                )}
                            >
                                {step.label}
                            </span>
                        </button>
                        {index < steps.length - 1 && (
                            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default CampaignStepper;
